/**
 * Model instructions that must never reach the DOM (F6).
 *
 * These four `facts` keys are prompt scaffolding for the sentence generator, not copy.
 * Their values read like editorial guidance — e.g. `direction_note` on the live
 * Verde Ranch payload is *"Use the position_direction string VERBATIM. Do not derive
 * direction from avg_position…"* — so a single careless `JSON.stringify(facts)` in a
 * future panel would print an instruction to an owner on a customer-facing report.
 *
 * `search_console/lib/modelNotes.test.js` covers the stripper; `SearchConsolePage.test.js`
 * additionally renders the full fixture and asserts these values appear nowhere in
 * `document.body.textContent`, which is the control that catches the careless case.
 *
 * NOT in this list, deliberately:
 *   - `demand_season.summary.note` — reads like an instruction but F6's list is
 *     explicit and does not include it. It is suppressed at the component instead
 *     (`SeasonOverlay`), pending confirmation of whether F6's list is exhaustive or a
 *     `*_note` naming convention.
 *   - `timezone_note` — owner-readable prose the spec wants surfaced (§10).
 */
export const HIDDEN_FACT_KEYS = ['means', 'do_not_infer', 'direction_note', 'interpretation_note'];

/**
 * Return a copy of `facts` with every model-instruction key removed.
 *
 * Shallow by design: the four keys only ever appear at the top level of `facts`, and a
 * deep clone would also copy 90 days of trend rows on every render.
 */
export const stripModelNotes = (facts) => {
  if (!facts || typeof facts !== 'object' || Array.isArray(facts)) return facts;
  const out = {};
  for (const key of Object.keys(facts)) {
    if (!HIDDEN_FACT_KEYS.includes(key)) out[key] = facts[key];
  }
  return out;
};

/** Fields that are diagnostic-only and must never be displayed or sorted on (F15). */
export const NEVER_DISPLAYED = [
  'salience',
  'sentence_source',
  'brand_cores',
  'search_types_with_no_data',
  'client_id',
];
